import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { signInWithCustomToken } from 'firebase/auth';
import { auth, functions } from '../lib/firebase';
import Wordmark from '../components/Wordmark';

/**
 * RecoverAccountPage — landing page for the recovery link emailed by
 * `selfSuspendAccount` (ADMIN_SPEC §13).
 *
 * URL: /recover-account/:token
 *
 * Flow on mount (no user interaction):
 *   1. Read token from URL.
 *   2. Call `selfCancelDeletion({ token })` — server validates the
 *      token, clears the suspended fields on account/main, re-
 *      enables Firebase Auth, mints a custom token, deletes the
 *      recovery doc, audit-logs.
 *   3. signInWithCustomToken with the returned token → user is
 *      signed back in to their original uid.
 *   4. Full-page reload to / so the rest of the app boots cleanly
 *      with the restored auth + un-suspended Firestore writes.
 *
 * No sign-in widget — the recovery token IS the proof of identity
 * (only the inbox-holder for the suspended user's email could have
 * received the link). Mirrors the trust model of magic-link sign-in.
 */
export default function RecoverAccountPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('working'); // 'working' | 'success' | 'error'
  const [errorMsg, setErrorMsg] = useState('');
  const calledRef = useRef(false);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMsg('No recovery token in URL.');
      return;
    }
    if (calledRef.current) return;
    calledRef.current = true;
    (async () => {
      try {
        const fn = httpsCallable(functions, 'selfCancelDeletion');
        const res = await fn({ token });
        const data = res?.data || {};
        if (!data.ok || !data.customToken) {
          throw new Error('server returned an unexpected response');
        }
        await signInWithCustomToken(auth, data.customToken);
        // Server-side soft success: account was already restored
        // (e.g. admin clicked cancel, or this recovery link was
        // followed once before but the redirect didn't complete).
        // Same UX as a normal restore — sign the user in and bounce
        // them home — but the success copy acknowledges they're
        // already back so it doesn't feel like the click "did
        // anything new".
        setStatus(data.alreadyRestored ? 'already-restored' : 'success');
        // Full-page reload — see the AcceptInvitePage comment for
        // why this is preferable to an SPA navigate after a fresh
        // sign-in.
        setTimeout(() => {
          window.location.href = '/';
        }, 1200);
      } catch (err) {
        console.error('[RecoverAccountPage] failed', err);
        setStatus('error');
        setErrorMsg(err?.message || 'Could not restore your account.');
      }
    })();
  }, [token]);

  return (
    <div style={{
      minHeight: '100vh', display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      background: 'var(--bg)', padding: '20px',
    }}>
      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        padding: '40px 44px 36px',
        maxWidth: '420px',
        width: '100%',
        textAlign: 'center',
      }}>
        <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'center' }}>
          <Wordmark fontSize={26} scaleWithUi={false} />
        </div>

        {status === 'working' && (
          <>
            <div style={{
              fontFamily: 'var(--mono)', fontSize: 16, fontWeight: 600,
              color: 'var(--accent)', marginBottom: 8,
            }}>
              restoring your account…
            </div>
            <div style={{
              fontFamily: 'var(--mono)', fontSize: 13,
              color: 'var(--text-dim)', lineHeight: 1.6,
            }}>
              one moment while we re-enable your sign-in and roll back the deletion.
            </div>
          </>
        )}

        {status === 'success' && (
          <>
            <div style={{
              fontFamily: 'var(--mono)', fontSize: 16, fontWeight: 600,
              color: 'var(--accent)', marginBottom: 8,
            }}>
              ✓ welcome back
            </div>
            <div style={{
              fontFamily: 'var(--mono)', fontSize: 13,
              color: 'var(--text-dim)', lineHeight: 1.6,
            }}>
              your account is restored. taking you to your boards…
            </div>
          </>
        )}

        {status === 'already-restored' && (
          <>
            <div style={{
              fontFamily: 'var(--mono)', fontSize: 16, fontWeight: 600,
              color: 'var(--accent)', marginBottom: 8,
            }}>
              ✓ already restored
            </div>
            <div style={{
              fontFamily: 'var(--mono)', fontSize: 13,
              color: 'var(--text-dim)', lineHeight: 1.6,
            }}>
              your account was already restored (admin or earlier click).
              signing you in and taking you to your boards…
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div style={{
              fontFamily: 'var(--mono)', fontSize: 16, fontWeight: 600,
              color: 'var(--danger, #d04a4a)', marginBottom: 8,
            }}>
              couldn't restore the account
            </div>
            <div style={{
              fontFamily: 'var(--mono)', fontSize: 13,
              color: 'var(--text-dim)', lineHeight: 1.6,
              marginBottom: 16,
            }}>
              {errorMsg || 'this recovery link may have expired (recovery is only valid for 7 days from deletion) or was already used.'}
            </div>
            <button
              type="button"
              onClick={() => navigate('/', { replace: true })}
              style={{
                background: 'transparent',
                border: '1px solid var(--border-bright)',
                color: 'var(--text-dim)',
                fontFamily: 'var(--mono)',
                fontSize: 13,
                padding: '10px 16px',
                borderRadius: 'var(--radius)',
                cursor: 'pointer',
              }}
            >
              go to listicity
            </button>
          </>
        )}
      </div>
    </div>
  );
}
